/**
 * Datasets Page - Main page for dataset management
 *
 * Displays all datasets in a DataTable with sorting and pagination. Clicking
 * a row opens the dataset details.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Download, Eye, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { DeleteDatasetDialog } from '@/components/datasets/DeleteDatasetDialog';
import { useDatasets, fetchAllDatasetPages } from '@/hooks/useDatasets';
import { useToast } from '@/hooks/useToast';
import { formatBytes } from '@/lib/utils';
import { downloadCsv, type CsvExportColumn } from '@/lib/utils/csv-export';
import type { Dataset } from '@/types';

/**
 * Datasets Page Component
 */
export const Datasets: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const page = Number(searchParams.get('page')) || 1;
  const size = Number(searchParams.get('size')) || 20;
  const sort = searchParams.get('sort') || 'created_at';
  const order = (searchParams.get('order') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc';

  // Local, immediately-responsive search box; the request is debounced (#964).
  const [searchInput, setSearchInput] = useState<string>(searchQuery);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<{ id: string; name: string } | null>(null);

  // Debounce the search box into the URL, which drives the request (#964).
  useEffect(() => {
    if (searchInput === searchQuery) return;
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.delete('page'); // a new search starts at page 1
          return next;
        },
        { replace: true }
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery, setSearchParams]);

  // #964: one server-paginated request per view. The library used to pull every
  // page on mount (3 requests / 1.16MB / ~15s cold on a 1,143-dataset catalog);
  // the API serializes catalog reads, so fewer requests is the only lever.
  const { data, isLoading, error, refetch } = useDatasets({
    page,
    size,
    search: searchQuery || undefined,
    sort,
    order,
  });

  const handleViewDetails = useCallback((id: string) => {
    navigate(`/datasets/${id}`);
  }, [navigate]);

  const handleDelete = useCallback((id: string, name: string) => {
    setDatasetToDelete({ id, name });
    setDeleteDialogOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'Dataset list has been refreshed',
    });
  }, [refetch, toast]);

  const handleCreateNew = useCallback(() => {
    navigate('/datasets/create');
  }, [navigate]);

  /** Sort state → URL params; re-sorting starts over at page 1. */
  const handleSortingChange = useCallback(
    (next: { id: string; desc: boolean }[]) => {
      const first = next[0];
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (!first) {
            params.delete('sort');
            params.delete('order');
          } else {
            params.set('sort', first.id);
            params.set('order', first.desc ? 'desc' : 'asc');
          }
          params.delete('page');
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** Pagination state → URL params (page is 1-indexed in the URL). */
  const handlePaginationChange = useCallback(
    (next: { pageIndex: number; pageSize: number }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('page', String(next.pageIndex + 1));
          params.set('size', String(next.pageSize));
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /**
   * Export every dataset matching the active search, not just the loaded page.
   *
   * The table now holds one server page, so the export re-fetches the full
   * matching set first (#964) — the expensive all-pages pull moves off first
   * paint and onto this explicit, rare action.
   */
  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    try {
      const all = await fetchAllDatasetPages({
        search: searchQuery || undefined,
        sort,
        order,
      });
      const csvColumns: CsvExportColumn<Dataset>[] = [
        { header: 'Name', accessor: (d) => d.name },
        { header: 'Description', accessor: (d) => d.description ?? '' },
        { header: 'Source', accessor: (d) => d.source_name ?? '' },
        { header: 'Format', accessor: (d) => (d as any).format ?? '' },
        { header: 'Size (bytes)', accessor: (d) => d.size_bytes ?? '' },
        { header: 'Rows', accessor: (d) => d.row_count ?? '' },
        { header: 'Created', accessor: (d) => d.created_at ?? '' },
      ];
      downloadCsv(all.items, csvColumns, `datasets-${format(new Date(), 'yyyy-MM-dd')}`);
    } catch (exportError: any) {
      toast({
        title: 'Export Failed',
        description:
          exportError instanceof Error ? exportError.message : 'Failed to export datasets',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [searchQuery, sort, order, toast]);

  const columns = useMemo<ColumnDef<Dataset>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableSortHeader column={column}>Name</DataTableSortHeader>,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.description || 'No description'}
          </span>
        ),
      },
      {
        accessorKey: 'source_name',
        header: ({ column }) => <DataTableSortHeader column={column}>Source</DataTableSortHeader>,
        cell: ({ row }) => row.original.source_name || 'N/A',
      },
      {
        accessorKey: 'format',
        header: ({ column }) => <DataTableSortHeader column={column}>Format</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = (row.original as any).format;
          if (!value) return null;
          return (
            <Badge variant="secondary" className="uppercase">
              {value}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'size_bytes',
        header: ({ column }) => <DataTableSortHeader column={column}>Size</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = row.original.size_bytes;
          if (!value) return <span className="text-muted-foreground">N/A</span>;
          return formatBytes(value);
        },
      },
      {
        accessorKey: 'row_count',
        header: ({ column }) => <DataTableSortHeader column={column}>Rows</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = row.original.row_count;
          if (!value) return <span className="text-muted-foreground">N/A</span>;
          return value.toLocaleString();
        },
      },
      {
        accessorKey: 'created_at',
        header: ({ column }) => <DataTableSortHeader column={column}>Created</DataTableSortHeader>,
        cell: ({ row }) => {
          const value = row.original.created_at;
          if (!value) return null;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm');
          } catch {
            return value;
          }
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(row.original.id);
              }}
              className="h-8 w-8 p-0"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id, row.original.name);
              }}
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [handleViewDetails, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Datasets</h1>
          <p className="text-muted-foreground mt-1">
            Browse and manage your datasets
          </p>
        </div>
        <Button onClick={handleCreateNew} size="lg">
          <Plus className="h-4 w-4 mr-2" />
          New Dataset
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search datasets..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load datasets: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* DataTable */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Loading…
        </div>
      ) : (
        <DataTable
          data={data?.items ?? []}
          columns={columns}
          onRowClick={(d) => navigate(`/datasets/${d.id}`)}
          getRowId={(d) => d.id}
          manualPagination
          manualSorting
          pageCount={data?.pages ?? 0}
          totalRows={data?.total ?? 0}
          sorting={[{ id: sort, desc: order === 'desc' }]}
          pagination={{ pageIndex: page - 1, pageSize: size }}
          onSortingChange={handleSortingChange}
          onPaginationChange={handlePaginationChange}
          emptyState={
            searchQuery
              ? `No datasets match "${searchQuery}".`
              : 'No datasets yet.'
          }
        />
      )}

      {/* Delete (soft) / permanent-delete dialog */}
      <DeleteDatasetDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        dataset={datasetToDelete}
        onDeleted={() => setDatasetToDelete(null)}
      />
    </div>
  );
};
